import React, { useState, useContext } from 'react';
import CustomToolbarButton from './toolbar/custom-toolbar-button';
import ASEmailIcon from '../../../assets/icons/Email.svg';
// import ActionDialog from './action-dialog';
import { useSendEmail } from './use-send-email';
import ValidationUtils from '../../../react-core-lib/utils/validation-utils';
import mixpanel from 'mixpanel-browser';
import SuccessDialog from '../../UIControls/api-button/SuccessDialog';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import StoreContext from '../../../react-core-lib/components/app/store-context';

import BlockingSpinner from '../../UIControls/BlockingSpinner';
import PatientDetailsDialog from './patient-details-dialog';
import { notifyAuthExpired, isAuthExpiredDialogVisible, isAuthExpiredError } from '../../../utils/auth-error-utils';
import { useUnsavedChanges } from '../UnsavedChangesContext';

function HandoutEmailButton({
    handoutData,
    currentUser,
    bundledHandoutsView = false,
    accountType,
    onUpgradeRequired,
    toggleStates,
    generateLink,
    shouldShowPatientDialog = () => false,
}) {
    const { userDataStore, authStore } = useContext(StoreContext);
    const { hasUnsavedChanges, promptBeforeAction } = useUnsavedChanges();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorDialogTitle, setErrorDialogTitle] = useState('Email failed to send');
    const [errorDialogMessage, setErrorDialogMessage] = useState('Please try again');
    const [showReloadAction, setShowReloadAction] = useState(false);
    // Patient details dialog state
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [pendingPatientDetails, setPendingPatientDetails] = useState(null);

    const sendEmail = useSendEmail();
    // const theme = useTheme();

    const openAuthAwareErrorDialog = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'handout-email' });
            return;
        }
        setErrorDialogTitle('Email failed to send');
        setErrorDialogMessage('Please try again');
        setShowReloadAction(false);
        setOpenErrorDialog(true);
    };

    const handleDialogSend = async () => {
        try {
            // Generate the linkId with patient details if available
            const linkId = await generateLink(pendingPatientDetails);
            console.log('>>> Generated linkId:', linkId);
            const clinicId = userDataStore.userId;

            const result = await sendEmail(
                email,
                bundledHandoutsView ? 'your handouts' : handoutData.contentName,
                linkId,
                clinicId,
                toggleStates,
            );

            // Assuming successful response means the email is sent
            if (result) {
                mixpanel.track('HandoutShared', {
                    email: currentUser.attributes.email,
                    shareMethod: 'Send Email',
                    handoutTitle: handoutData.contentName,
                });
                setOpenSuccessDialog(true);
                // Clear pending patient details after successful send
                setPendingPatientDetails(null);
            } else {
                openAuthAwareErrorDialog();
            }
        } catch (error) {
            console.error('Error while sending email:', error);
            openAuthAwareErrorDialog(error);
        }
    };

    const handlePatientDetailsSubmit = async (patientDetails) => {
        // Save patient details and proceed to email dialog
        setPendingPatientDetails(patientDetails);
        setPatientDialogOpen(false);

        // Proceed to email dialog with patient details stored
        if (isAuthExpiredDialogVisible()) {
            return;
        }
        setDialogOpen(true);
    };

    const handleClick = async () => {
        // Check auth expiration first
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        // Check Free/Student account BEFORE unsaved changes
        if (accountType === 'Free' || accountType === 'Student') {
            onUpgradeRequired();
            return;
        }

        const executeEmailAction = async () => {
            setLoading(true);
            try {
                // Check if we should show patient dialog first (for "My Library" content)
                if (typeof shouldShowPatientDialog === 'function' ? shouldShowPatientDialog() : false) {
                    setPatientDialogOpen(true);
                } else {
                    // For "My Clinic" content, proceed directly to email dialog

                    setDialogOpen(true);
                }
            } catch (error) {
                console.error('Error prefilling from ProVet:', error);
            } finally {
                setLoading(false);
            }
        };

        // Check for unsaved changes before proceeding
        if (hasUnsavedChanges) {
            promptBeforeAction(
                executeEmailAction,
                "Your changes haven't been saved. Do you want to save before sharing?",
            );
        } else {
            await executeEmailAction();
        }
    };

    return (
        <>
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => {
                    setPatientDialogOpen(false);
                    setPendingPatientDetails(null);
                }}
                onSubmit={handlePatientDetailsSubmit}
            />
            <BlockingSpinner open={loading} />
            <CustomToolbarButton
                iconSrc={ASEmailIcon}
                tooltipTitle={bundledHandoutsView ? 'Email handouts' : 'Email handout'}
                onClick={handleClick}
            />

            <ConfirmationDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                onConfirm={handleDialogSend}
                title="Send via email"
                message="Please enter your email address."
                confirmText="Send"
                cancelText="Cancel"
                cancelTextReq={true}
                showTextField={true}
                textFieldLabel="Email address"
                textFieldValue={email}
                onTextFieldChange={(e) => setEmail(e.target.value)}
                validate={ValidationUtils.validateEmail}
            />

            <SuccessDialog
                open={openSuccessDialog}
                handleClose={() => {
                    setOpenSuccessDialog(false);
                }}
                title="Email sent"
                message="The email was sent successfully."
            />
            <ErrorDialog
                open={openErrorDialog}
                handleClose={() => {
                    setOpenErrorDialog(false);
                    setShowReloadAction(false);
                }}
                title={errorDialogTitle}
                message={errorDialogMessage}
                primaryActionText={showReloadAction ? 'Reload Page' : null}
                onPrimaryAction={showReloadAction ? () => window.location.reload() : null}
            />
        </>
    );
}

export default HandoutEmailButton;
